import type { CardType, WebSockets } from '@pushflow/types'
import { clsx } from 'clsx'
import { useAtomValue } from 'jotai'
import { useEffect } from 'react'
import { useAnal, useAuthWebSocket } from '../../../../hooks'
import { userSettingsAtom } from '../../../../store/atoms'
import type { GameDataType } from '../../../../types'
import { Button, Card, CountdownBar } from '../../../atoms'
import { ETabBorderType } from '../enum'
import * as styles from '../index.css'
import type { UnhandledMessageType } from '../types'

export const TableTab = (props: {
  tableId: string
  password: string | null
  type?: ETabBorderType
  cards?: CardType[]
  name?: string
  turnRemainSeconds?: number
  turnSeconds?: number
  isHidden: boolean
  isActive: boolean
  onUpdate: (data: GameDataType) => void
  onUnhandledUpdate: (data: UnhandledMessageType) => void
  onClick: React.MouseEventHandler<HTMLButtonElement>
}) => {
  const { logEvent } = useAnal()
  const { isAuthorizied, sendJsonMessage, lastJsonMessage } =
    useAuthWebSocket<WebSockets.TableWebSocketOutputType>(
      `table/${props.tableId}`,
    )
  const userSettings = useAtomValue(userSettingsAtom)

  useEffect(() => {
    if (!isAuthorizied) {
      return
    }

    const connectMessage: WebSockets.ConnectTableWebSocketInputType = {
      type: 'connect',
      data: props.password
        ? {
            password: props.password,
          }
        : null,
    }
    sendJsonMessage(connectMessage)
  }, [isAuthorizied, props.password])

  useEffect(() => {
    if (lastJsonMessage === null) return
    console.log(lastJsonMessage)

    switch (lastJsonMessage.type) {
      case 'connect': {
        logEvent('connected_to_table', {
          id: lastJsonMessage.data.info.id,
        })
        props.onUpdate(lastJsonMessage.data)
        break
      }
      case 'update': {
        props.onUpdate(lastJsonMessage.data)
        break
      }
      default:
        props.onUnhandledUpdate(lastJsonMessage)
        break
    }
  }, [lastJsonMessage])

  return (
    <Button
      className={clsx(
        styles.tableTabBorderByType[props.type || ETabBorderType.DEFAULT],
        styles.tableTab,
        props.isActive ? styles.activeTable : undefined,
      )}
      style={{
        display: props.isHidden ? 'none' : 'grid',
      }}
      variant='reset'
      onClick={props.onClick}
    >
      {props.turnRemainSeconds !== undefined && (
        <div className={styles.timerBar}>
          <CountdownBar
            seconds={props.turnRemainSeconds}
            fullSeconds={props.turnSeconds}
          />
        </div>
      )}
      {props.cards ? (
        <div
          className={styles.cards}
          style={
            {
              '--card-count': props.cards?.length ?? 0,
              gap: props.cards && props.cards.length > 2 ? 0 : '0.25rem',
            } as React.CSSProperties
          }
        >
          {props.cards.map((card) => (
            <Card
              key={card}
              value={card}
              theme={userSettings.tableSettings.cardsType}
              cardBack={userSettings.tableSettings.cardsBack}
              className={styles.card}
            />
          ))}
        </div>
      ) : (
        <span className={styles.tableName}>{props.name}</span>
      )}
    </Button>
  )
}
